import axios from 'axios'
import { getApiBaseUrl } from '@/config/apiBase'
import { useAuthStore } from '@/stores/authStore'
import { isPublicApiRequest, waitForApiSession } from '@/utils/waitForApiSession'
import { isAuthRefreshNoSession, postAuthRefresh } from '@/services/authRefresh'
import { getCsrfToken, clearCsrfTokenCache } from '@/services/csrf'
import * as socketService from '@/services/socket'
import { shouldRetryCsrfRequest } from '@/services/apiRetryPolicy'

export { getCsrfToken } from '@/services/csrf'

const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
})

// Interceptor de request — aguarda bootstrap e injeta Bearer (evita 401 em mount)
api.interceptors.request.use(
  async (config) => {
    const url = typeof config.url === 'string' ? config.url : ''
    const safeMethods = ['GET', 'HEAD', 'OPTIONS']
    const isSafe = safeMethods.includes((config.method ?? 'GET').toUpperCase())
    if (!isSafe) {
      const csrf = await getCsrfToken()
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf
      }
    }

    if (!isPublicApiRequest(url)) {
      try {
        const token = await waitForApiSession()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch (err) {
        return Promise.reject(err)
      }
    } else {
      const token = useAuthStore.getState().token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      // Deixa o browser definir multipart/form-data com boundary correto.
      if (config.headers && 'Content-Type' in config.headers) {
        delete config.headers['Content-Type']
      }
    }

    return config
  },
  (error) => Promise.reject(error),
)

// Refresh token queue — evita múltiplos refreshes concorrentes
let isRefreshing = false
let loginRedirectScheduled = false
let failedQueue: Array<{
  resolve: (value: unknown) => void
  reject: (reason?: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve(token)
    }
  })
  failedQueue = []
}

// Interceptor de response — trata 401 com refresh automático
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    const status = error.response?.status
    const errorCode = error.response?.data?.error?.code
    const requestUrl = typeof originalRequest?.url === 'string' ? originalRequest.url : ''

    if (
      status === 429 &&
      !originalRequest?._rateRetry &&
      ['GET', 'HEAD'].includes((originalRequest?.method ?? 'GET').toUpperCase())
    ) {
      originalRequest._rateRetry = true
      await new Promise((resolve) => setTimeout(resolve, 1_500))
      return api(originalRequest)
    }

    if (shouldRetryCsrfRequest(status, errorCode, Boolean(originalRequest?._csrfRetry))) {
      clearCsrfTokenCache()
      originalRequest._csrfRetry = true
      return api(originalRequest)
    }

    if (status === 401 && !originalRequest?._retry && !isPublicApiRequest(requestUrl)) {
      const isPublicPath =
        window.location.pathname === '/login' ||
        window.location.pathname.startsWith('/portal/') ||
        window.location.pathname.startsWith('/p/') ||
        window.location.pathname.startsWith('/pj/cadastro/')

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`
          } else {
            delete originalRequest.headers.Authorization
          }
          return api(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const resp = await postAuthRefresh()
        if (isAuthRefreshNoSession(resp.status)) {
          throw new Error('NO_REFRESH_SESSION')
        }

        const newToken = resp.data?.data?.token
        const user = resp.data?.data?.user
        if (!user) {
          throw new Error('NO_REFRESH_SESSION')
        }

        useAuthStore.getState().login(user, newToken ?? '')
        socketService.reconnect()
        processQueue(null, newToken ?? '')

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
        } else {
          delete originalRequest.headers.Authorization
        }
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        void useAuthStore.getState().logout().finally(() => {
          if (!isPublicPath && window.location.pathname !== '/login' && !loginRedirectScheduled) {
            loginRedirectScheduled = true
            window.location.replace('/login')
          }
        })
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

if (typeof window !== 'undefined') {
  void getCsrfToken()
}

export default api
